import { ArrowLeft, ArrowRight, Button, Delete, Heading } from '@core/ui';
import { t } from 'i18next';
import { Duration } from 'luxon';
import { useEffect, useState } from 'react';
import { SlideTimelineEvent, Timeline } from '../../../parser';
import { TimestampInput } from '../../molecules';

export const SlideEditorOrganism = ({ timeline, currentTime, onSeek }: { timeline: Timeline; currentTime: Duration; onSeek: (seek: Duration) => void }) => {
    const [previousSlide, setPreviousSlide] = useState<SlideTimelineEvent>();
    const [nextSlide, setNextSlide] = useState<SlideTimelineEvent>();
    const [currentSlide, setCurrentSlide] = useState<SlideTimelineEvent>();
    const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(-1);

    useEffect(() => {
        if (!timeline) {
            setPreviousSlide(undefined);
            setCurrentSlide(undefined);
            setNextSlide(undefined);
            return;
        }

        let prevSlide: SlideTimelineEvent | undefined = undefined;
        let currSlide: SlideTimelineEvent | undefined = undefined;
        let currIndex: number = -1;
        let nextSlide: SlideTimelineEvent | undefined = undefined;
        for (let i = 0; i < timeline.slides.length; i++) {
            if (timeline.slides[i].timestamp <= currentTime) {
                if (i > 0) {
                    prevSlide = timeline.slides[i - 1];
                }

                currSlide = timeline.slides[i];
                currIndex = i;

                if (i < timeline.slides.length - 1) {
                    nextSlide = timeline.slides[i + 1];
                }
            }
        }

        if (!currSlide && timeline.slides.length > 0) {
            nextSlide = timeline.slides[0];
        }

        setPreviousSlide(prevSlide);
        setCurrentSlide(currSlide);
        setCurrentSlideIndex(currIndex);
        setNextSlide(nextSlide);
    }, [timeline, currentTime]);

    const onDelete = () => {
        if (currentSlideIndex === -1) {
            return;
        }

        timeline.slides.splice(currentSlideIndex, 1);
        timeline.slides = [...timeline.slides];
        timeline.changed = true;
    };

    return (
        <div className="border border-gray-500 flex flex-col gap-4">
            <div className="flex-1 flex flex-col">
                <div className="flex items-center justify-between gap-2 mb-4 p-4">
                    <Heading variant="h2">{t('Editor.Slides.Heading')}</Heading>
                    {currentSlide && (
                        <>
                            {/*<Button variant="primary-outline" iconbtn classNames="ms-auto">
                                <Replace classNames="size-6 flex-none" />
                                <span className="sr-only">{t('Editor.Slides.Button.Replace')}}</span>
                            </Button>*/}
                            <Button variant="warning-outline" onClick={onDelete} iconbtn classNames="ms-auto">
                                <Delete classNames="size-6 flex-none stroke-current" />
                                <span className="sr-only">{t('Editor.Slides.Button.Delete')}</span>
                            </Button>
                        </>
                    )}
                </div>
                <div className="relative flex-1 bg-gray-100 grid items-end justify-items-center">
                    <div className="relative col-start-1 row-start-1 flex items-center">
                        <Button
                            variant="primary"
                            iconbtn
                            onClick={() => onSeek(previousSlide?.timestamp || Duration.fromMillis(0))}
                            disabled={!previousSlide}
                            classNames="absolute left-4"
                        >
                            <span className="sr-only">{t('Slide.Button.Previous')}</span>
                            <ArrowLeft classNames="size-6 fill-current" />
                        </Button>
                        <img src={currentSlide?.url ?? '../images/video-background.webp'} className="min-w-0 select-none" />
                        <Button
                            variant="primary"
                            iconbtn
                            onClick={() => onSeek(nextSlide?.timestamp || Duration.fromMillis(0))}
                            disabled={!nextSlide}
                            classNames="absolute right-4"
                        >
                            <span className="sr-only">{t('Slide.Button.Next')}</span>
                            <ArrowRight classNames="size-6 fill-current" />
                        </Button>
                    </div>
                    {currentSlide && (
                        <div className="col-start-1 xl:row-start-1 xl:bg-gray-100/85 xl:rounded-t-lg p-1 z-1">
                            <TimestampInput timestamp={currentSlide.timestamp} onChange={(timestamp) => (currentSlide.timestamp = timestamp)} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
